import React, { useEffect } from 'react';
import { Check, Sparkles, Upload, X } from 'lucide-react';

export default function VisionDemoLandingPage({ onLaunchDemo }) {
  useEffect(() => {
    document.title = 'Vision Demo | Choose Explanation Route';
  }, []);

  const demoInstructionChoices = [
    {
      id: 'direct',
      title: 'Use Vision to get new jobs and candidates directly',
      body: 'Start with the demo as if Vision is bringing new listings and candidate opportunities into the workspace for your recruiters to qualify.',
      instructions: [
        'Review the live role and candidate pipeline Vision creates.',
        'Use matching, screening, shortlist, and reporting views to see how new work is handled.',
      ],
      icon: Sparkles,
    },
    {
      id: 'import',
      title: 'Upload your jobs and candidates into Vision',
      body: 'Start with the demo as if you already receive jobs and applicants from your website, inbox, ATS, job boards, or spreadsheets.',
      instructions: [
        'Use the sample upload and intake flow as the model for your existing applicant sources.',
        'Map imported candidates to roles, then continue through screening and shortlist review.',
      ],
      icon: Upload,
    },
  ];

  const handleClose = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#050914] px-4 py-6 text-slate-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_22%,rgba(34,211,238,0.16),transparent_28%),radial-gradient(circle_at_85%_0%,rgba(14,165,233,0.13),transparent_30%),linear-gradient(135deg,#071225_0%,#060b18_48%,#020617_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:46px_46px] opacity-40" />
      <div className="absolute inset-0 backdrop-blur-[2px]" />

      <section className="relative z-10 mx-auto max-w-4xl rounded-shell border border-white/10 bg-[#0a1020]/95 p-5 shadow-2xl shadow-black/50 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Before you use the demo</div>
            <h1 className="mt-2 text-2xl font-black leading-tight text-white sm:text-3xl">Choose how Vision should be explained.</h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
              Pick the route that matches how your agency wants to bring jobs and candidates into Vision. The demo opens with sample data after you choose.
            </p>
          </div>
          <button type="button" onClick={handleClose} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-300 transition hover:text-white" aria-label="Close demo instructions">
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {demoInstructionChoices.map((choice) => {
            const ChoiceIcon = choice.icon;
            return (
              <article key={choice.id} className="rounded-card border border-white/10 bg-white/[0.04] p-5">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-card border border-cyan-300/25 bg-cyan-300/10 text-cyan-200">
                    <ChoiceIcon size={20} />
                  </div>
                  <div>
                    <h2 className="text-base font-black text-white">{choice.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{choice.body}</p>
                  </div>
                </div>
                <div className="mt-5 space-y-3">
                  {choice.instructions.map((instruction) => (
                    <div key={instruction} className="flex gap-3 text-sm leading-6 text-slate-300">
                      <Check size={16} className="mt-1 shrink-0 text-cyan-300" />
                      <span>{instruction}</span>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => onLaunchDemo(choice.id)} className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300">
                  Start demo this way
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
